import { zodResolver } from '@hookform/resolvers/zod';
import { ActionCommitmentStatus } from 'further-types/lp/action';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  IncomeOrExpenseApportionmentMode,
  IncomeOrExpenseState,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';
import { incomeAndExpenses as rules } from 'further-ui/validations';
import { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = rules.CreateIncomeOrExpense;

export type FormValues = z.infer<typeof formSchema> & {
  switchDate?: CalendarDay;
};

type Props = {
  values?: Omit<FormValues, 'apportionmentMode'> & {
    apportionmentMode: IncomeOrExpenseApportionmentMode;
  };
  vehicleId: string;
  baseCurrency?: CurrencyCode;
  defaultCategory?: IncomeOrExpenseType;
  isSwitchMode?: boolean;
};

const FormWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  values,
  vehicleId,
  baseCurrency,
  defaultCategory,
  isSwitchMode,
}) => {
  const getDefaultValues = () => {
    if (isSwitchMode && values) {
      return {
        ...values,
        incomeOrExpenseState:
          values.category === IncomeOrExpenseType.Expense
            ? IncomeOrExpenseState.Payable
            : IncomeOrExpenseState.Receivable,
        switchDate: new CalendarDay(new Date()),
      };
    }

    return (
      values ?? {
        vehicleId,
        apportionmentMode: IncomeOrExpenseApportionmentMode.Single,
        startDate: new CalendarDay(new Date()),
        currencyCode: baseCurrency,
        apportionmentPeriods: [],
        category: defaultCategory ?? IncomeOrExpenseType.Income,
        allocationRule: AllocationRule.ByCommitmentAndClosingDate,
        commitmentStatus:
          defaultCategory === IncomeOrExpenseType.Expense
            ? ActionCommitmentStatus.InsideCommitment
            : undefined,
      }
    );
  };

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  return <FormProvider {...formMethods}>{children}</FormProvider>;
};

export default FormWrapper;
